import React, { createContext, useState, useContext, ReactNode, useCallback, useRef, useEffect } from 'react';
import {
  CoordinatePatient,
  CoordinateProvider as ProviderType,
  CoordinateContextType,
  CoordinationStatus,
  DispositionType,
  ClinicalNeed,
  UrgencyLevel,
  ReferralPacket,
  ReferralDocument,
  DISPOSITION_LABELS,
  ActivityEntry,
  CaseTask,
  MilestoneKey,
  PatientContext,
  CaseBarrier,
  NegotiationCondition,
  ProviderNegotiation,
  AgentDraft,
  InsuranceTrack,
  ProviderOutreach,
  ProviderOutreachAttempt,
  CoordinateNotification,
} from '~/types/coordinate';
import { scoreProviders } from '~/utils/coordinateScoring';

export const CoordinateContext = createContext<CoordinateContextType | undefined>(undefined);

// ── Mock Provider Directory ──────────────────────────────────────────────────

const MOCK_PROVIDERS: ProviderType[] = [
  {
    id: 'prov-01',
    name: 'Sunrise Home Health',
    supportedDispositions: ['home_health', 'snf_at_home'],
    services: ['skilled_nursing', 'pt_ot', 'medication_management', 'wound_care'],
    acceptedInsurance: ['Medicare', 'Medicare Advantage', 'Medicaid', 'Blue Cross'],
    coverageZips: ['10001', '10002', '10003', '10010', '10011'],
    distanceMiles: 4.2,
    acceptanceLikelihood: 0.89,
    avgStartDays: 1,
    qualityRating: 4.8,
    healthSystemPreferred: true,
    currentCapacitySignal: 'high',
  },
  {
    id: 'prov-02',
    name: 'CareFirst Home Services',
    supportedDispositions: ['home_health', 'palliative'],
    services: ['skilled_nursing', 'medication_management', 'cardiac_monitoring', 'pt_ot'],
    acceptedInsurance: ['Medicare', 'Medicare Advantage', 'Aetna', 'United'],
    coverageZips: ['10001', '10004', '10005', '10012'],
    distanceMiles: 7.1,
    acceptanceLikelihood: 0.82,
    avgStartDays: 2,
    qualityRating: 4.5,
    healthSystemPreferred: false,
    currentCapacitySignal: 'medium',
  },
  {
    id: 'prov-03',
    name: 'Metropolitan Home Care',
    supportedDispositions: ['home_health', 'snf_at_home', 'infusion_at_home'],
    services: ['skilled_nursing', 'wound_care', 'iv_therapy', 'medication_management'],
    acceptedInsurance: ['Medicare', 'Medicaid', 'Blue Cross', 'Cigna'],
    coverageZips: ['10002', '10003', '10006', '10007', '10013'],
    distanceMiles: 5.5,
    acceptanceLikelihood: 0.76,
    avgStartDays: 2,
    qualityRating: 4.3,
    healthSystemPreferred: true,
    currentCapacitySignal: 'high',
  },
  {
    id: 'prov-04',
    name: 'Coastal Care Partners',
    supportedDispositions: ['home_health', 'palliative'],
    services: ['pt_ot', 'speech_therapy', 'skilled_nursing', 'medication_management'],
    acceptedInsurance: ['Medicare Advantage', 'Blue Cross', 'Aetna', 'United'],
    coverageZips: ['10008', '10009', '10014', '10015'],
    distanceMiles: 11.3,
    acceptanceLikelihood: 0.71,
    avgStartDays: 3,
    qualityRating: 4.2,
    healthSystemPreferred: false,
    currentCapacitySignal: 'medium',
  },
  {
    id: 'prov-05',
    name: 'Harbor Health at Home',
    supportedDispositions: ['home_health', 'snf_at_home'],
    services: ['skilled_nursing', 'cardiac_monitoring', 'medication_management', 'wound_care'],
    acceptedInsurance: ['Medicare', 'Medicare Advantage', 'Medicaid'],
    coverageZips: ['10001', '10002', '10010', '10016'],
    distanceMiles: 8.9,
    acceptanceLikelihood: 0.68,
    avgStartDays: 3,
    qualityRating: 4.0,
    healthSystemPreferred: false,
    currentCapacitySignal: 'low',
  },
  {
    id: 'prov-06',
    name: 'Advanced Home Infusion',
    supportedDispositions: ['infusion_at_home'],
    services: ['iv_therapy', 'medication_management', 'skilled_nursing'],
    acceptedInsurance: ['Medicare', 'Medicare Advantage', 'Blue Cross', 'Cigna', 'Aetna'],
    coverageZips: ['10001', '10002', '10003', '10004', '10005', '10017'],
    distanceMiles: 6.0,
    acceptanceLikelihood: 0.91,
    avgStartDays: 1,
    qualityRating: 4.9,
    healthSystemPreferred: true,
    currentCapacitySignal: 'high',
  },
  {
    id: 'prov-07',
    name: 'Northside SNF Care',
    supportedDispositions: ['snf_at_home'],
    services: ['skilled_nursing', 'pt_ot', 'wound_care', 'cardiac_monitoring', 'medication_management'],
    acceptedInsurance: ['Medicare', 'Medicaid', 'Blue Cross'],
    coverageZips: ['10018', '10019', '10020', '10021'],
    distanceMiles: 14.2,
    acceptanceLikelihood: 0.79,
    avgStartDays: 4,
    qualityRating: 4.1,
    healthSystemPreferred: false,
    currentCapacitySignal: 'medium',
  },
  {
    id: 'prov-08',
    name: 'Comfort Care Hospice',
    supportedDispositions: ['hospice'],
    services: ['skilled_nursing', 'medication_management', 'speech_therapy'],
    acceptedInsurance: ['Medicare', 'Medicaid', 'Blue Cross', 'Aetna', 'United'],
    coverageZips: ['10001', '10002', '10003', '10010', '10022'],
    distanceMiles: 3.8,
    acceptanceLikelihood: 0.93,
    avgStartDays: 1,
    qualityRating: 4.9,
    healthSystemPreferred: true,
    currentCapacitySignal: 'high',
  },
  {
    id: 'prov-09',
    name: 'Eastside Home Health',
    supportedDispositions: ['home_health', 'palliative'],
    services: ['skilled_nursing', 'pt_ot', 'medication_management'],
    acceptedInsurance: ['Medicare Advantage', 'United', 'Cigna'],
    coverageZips: ['10023', '10024', '10025', '10026'],
    distanceMiles: 18.5,
    acceptanceLikelihood: 0.62,
    avgStartDays: 5,
    qualityRating: 3.8,
    healthSystemPreferred: false,
    currentCapacitySignal: 'high',
  },
  {
    id: 'prov-10',
    name: 'Wellbridge Palliative',
    supportedDispositions: ['palliative', 'hospice'],
    services: ['skilled_nursing', 'medication_management', 'cardiac_monitoring', 'pt_ot'],
    acceptedInsurance: ['Medicare', 'Medicare Advantage', 'Blue Cross', 'Cigna'],
    coverageZips: ['10001', '10003', '10005', '10027'],
    distanceMiles: 9.4,
    acceptanceLikelihood: 0.85,
    avgStartDays: 2,
    qualityRating: 4.6,
    healthSystemPreferred: true,
    currentCapacitySignal: 'medium',
  },
  {
    id: 'prov-11',
    name: 'Valley Home Health Group',
    supportedDispositions: ['home_health', 'snf_at_home'],
    services: ['skilled_nursing', 'wound_care', 'pt_ot', 'speech_therapy', 'medication_management'],
    acceptedInsurance: ['Medicare', 'Medicaid', 'Aetna'],
    coverageZips: ['10028', '10029', '10030', '10031'],
    distanceMiles: 22.1,
    acceptanceLikelihood: 0.74,
    avgStartDays: 3,
    qualityRating: 4.2,
    healthSystemPreferred: false,
    currentCapacitySignal: 'medium',
  },
  {
    id: 'prov-12',
    name: 'Premier Wound Care Services',
    supportedDispositions: ['home_health', 'snf_at_home'],
    services: ['wound_care', 'skilled_nursing', 'iv_therapy'],
    acceptedInsurance: ['Medicare', 'Medicare Advantage', 'Blue Cross', 'United'],
    coverageZips: ['10001', '10002', '10006', '10008', '10032'],
    distanceMiles: 7.7,
    acceptanceLikelihood: 0.88,
    avgStartDays: 2,
    qualityRating: 4.7,
    healthSystemPreferred: false,
    currentCapacitySignal: 'high',
  },
  {
    id: 'prov-13',
    name: 'Meridian Home Health',
    supportedDispositions: ['home_health', 'palliative'],
    services: ['skilled_nursing', 'cardiac_monitoring', 'medication_management', 'pt_ot'],
    acceptedInsurance: ['Medicare Advantage', 'Aetna', 'Cigna', 'United'],
    coverageZips: ['10033', '10034', '10035', '10036'],
    distanceMiles: 16.8,
    acceptanceLikelihood: 0.66,
    avgStartDays: 4,
    qualityRating: 3.9,
    healthSystemPreferred: false,
    currentCapacitySignal: 'low',
  },
  {
    id: 'prov-14',
    name: 'Apex Infusion Therapy',
    supportedDispositions: ['infusion_at_home'],
    services: ['iv_therapy', 'medication_management'],
    acceptedInsurance: ['Medicare', 'Blue Cross', 'Cigna', 'Aetna', 'United'],
    coverageZips: ['10001', '10004', '10007', '10009', '10037'],
    distanceMiles: 5.2,
    acceptanceLikelihood: 0.87,
    avgStartDays: 1,
    qualityRating: 4.8,
    healthSystemPreferred: false,
    currentCapacitySignal: 'high',
  },
  {
    id: 'prov-15',
    name: 'Community Palliative Care',
    supportedDispositions: ['palliative', 'hospice'],
    services: ['skilled_nursing', 'medication_management', 'speech_therapy', 'pt_ot'],
    acceptedInsurance: ['Medicare', 'Medicaid', 'Blue Cross'],
    coverageZips: ['10002', '10010', '10011', '10038'],
    distanceMiles: 10.1,
    acceptanceLikelihood: 0.80,
    avgStartDays: 2,
    qualityRating: 4.4,
    healthSystemPreferred: false,
    currentCapacitySignal: 'medium',
  },
  {
    id: 'prov-16',
    name: 'Greenfield SNF at Home',
    supportedDispositions: ['snf_at_home', 'home_health'],
    services: ['skilled_nursing', 'pt_ot', 'wound_care', 'cardiac_monitoring', 'medication_management', 'iv_therapy'],
    acceptedInsurance: ['Medicare', 'Medicare Advantage', 'Medicaid', 'Blue Cross'],
    coverageZips: ['10001', '10002', '10003', '10039'],
    distanceMiles: 6.4,
    acceptanceLikelihood: 0.84,
    avgStartDays: 2,
    qualityRating: 4.5,
    healthSystemPreferred: true,
    currentCapacitySignal: 'high',
  },
  {
    id: 'prov-17',
    name: 'Liberty Home Care',
    supportedDispositions: ['home_health'],
    services: ['skilled_nursing', 'medication_management', 'wound_care'],
    acceptedInsurance: ['Medicaid', 'United', 'Cigna'],
    coverageZips: ['10040', '10041', '10042', '10043'],
    distanceMiles: 25.3,
    acceptanceLikelihood: 0.58,
    avgStartDays: 6,
    qualityRating: 3.5,
    healthSystemPreferred: false,
    currentCapacitySignal: 'high',
  },
  {
    id: 'prov-18',
    name: 'Elite Cardiac Home Care',
    supportedDispositions: ['home_health', 'snf_at_home'],
    services: ['cardiac_monitoring', 'skilled_nursing', 'medication_management', 'pt_ot'],
    acceptedInsurance: ['Medicare', 'Medicare Advantage', 'Blue Cross', 'Aetna'],
    coverageZips: ['10001', '10005', '10012', '10044'],
    distanceMiles: 8.3,
    acceptanceLikelihood: 0.83,
    avgStartDays: 2,
    qualityRating: 4.6,
    healthSystemPreferred: false,
    currentCapacitySignal: 'medium',
  },
  {
    id: 'prov-19',
    name: 'Beacon Speech & Rehab',
    supportedDispositions: ['home_health', 'palliative'],
    services: ['speech_therapy', 'pt_ot', 'skilled_nursing'],
    acceptedInsurance: ['Medicare', 'Medicare Advantage', 'Blue Cross', 'United'],
    coverageZips: ['10002', '10003', '10008', '10045'],
    distanceMiles: 12.7,
    acceptanceLikelihood: 0.77,
    avgStartDays: 3,
    qualityRating: 4.3,
    healthSystemPreferred: false,
    currentCapacitySignal: 'medium',
  },
  {
    id: 'prov-20',
    name: 'Tristate Home Health Alliance',
    supportedDispositions: ['home_health', 'snf_at_home', 'palliative'],
    services: ['skilled_nursing', 'pt_ot', 'wound_care', 'iv_therapy', 'medication_management', 'cardiac_monitoring', 'speech_therapy'],
    acceptedInsurance: ['Medicare', 'Medicare Advantage', 'Medicaid', 'Blue Cross', 'Aetna', 'United', 'Cigna'],
    coverageZips: ['10001', '10002', '10003', '10004', '10005', '10010', '10011'],
    distanceMiles: 9.8,
    acceptanceLikelihood: 0.72,
    avgStartDays: 4,
    qualityRating: 4.0,
    healthSystemPreferred: false,
    currentCapacitySignal: 'low',
  },
];

// ── Mock Patient Data ────────────────────────────────────────────────────────

// insuranceType is omitted here and derived at runtime in buildInitialPatients
type PatientSeed = Omit<CoordinatePatient, 'insuranceType'> & { insuranceType?: CoordinatePatient['insuranceType'] };

const INITIAL_PATIENTS: PatientSeed[] = [
  // Home Health (7)
  {
    id: 'coord-01',
    name: 'Margaret Sullivan',
    age: 74,
    dob: '03/12/1951',
    address: '142 West 72nd St, New York, NY',
    zip: '10001',
    phone: '(212) 555-0142',
    insurance: 'Medicare',
    memberId: 'M1A-774-22891',
    disposition: 'home_health',
    clinicalNeeds: ['skilled_nursing', 'medication_management', 'pt_ot'],
    urgency: 'urgent',
    diagnoses: ['CHF exacerbation', 'Type 2 Diabetes', 'Hypertension'],
    recentNotes: 'Patient recovering from acute CHF exacerbation. Hemodynamically stable. Requires close medication titration and daily weights post-discharge. PT evaluation indicated for reconditioning.',
    coordinationStatus: 'pending',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Sarah Johnson',
    admitDate: '04/24/2026',
    attendingPhysician: 'Dr. Evelyn Park',
    losTargetDays: 5,
    barriers: [
      {
        id: 'barrier-01-1',
        type: 'missing_doc',
        description: 'Physician orders for home health services have not been signed. Dr. Park has been notified — orders needed before referral packet can be completed.',
        resolved: false,
        createdAt: '2026-04-25T09:00:00Z',
        linkedTaskId: 'task-01-1',
      },
      {
        id: 'barrier-01-2',
        type: 'missing_doc',
        description: 'PT evaluation note is not yet in the chart. Physical therapy must complete the functional assessment before matching can confirm appropriate PT/OT services.',
        resolved: false,
        createdAt: '2026-04-25T10:00:00Z',
        linkedTaskId: 'task-01-2',
      },
    ],
    tasks: [
      {
        id: 'task-01-1',
        title: 'Follow up with Dr. Park — home health orders not yet signed',
        status: 'open',
        priority: 'high',
        dueDate: '2026-05-01',
        assignee: 'Sarah Johnson',
        createdAt: '2026-04-30T09:01:00Z',
        isSystemGenerated: true,
        linkedBarrierId: 'barrier-01-1',
      },
      {
        id: 'task-01-2',
        title: 'Request PT evaluation note from therapy team',
        status: 'open',
        priority: 'normal',
        dueDate: '2026-05-02',
        assignee: 'Sarah Johnson',
        createdAt: '2026-04-30T10:01:00Z',
        isSystemGenerated: false,
        linkedBarrierId: 'barrier-01-2',
      },
    ],
    activityLog: [
      {
        id: 'ae-01-1',
        type: 'status_change',
        timestamp: '2026-04-24T08:00:00Z',
        author: 'System',
        content: 'Case opened. Patient admitted to Metro General Hospital.',
        isAgent: false,
      },
      {
        id: 'ae-01-2',
        type: 'agent',
        timestamp: '2026-04-25T09:00:00Z',
        author: 'Coordinate AI',
        content: 'Clinical documentation review complete. Two gaps identified: physician home health orders unsigned, PT evaluation note missing. Matching is blocked until both are resolved.',
        isAgent: true,
      },
    ],
  },
  {
    id: 'coord-02',
    name: 'Robert Chen',
    age: 68,
    dob: '07/29/1956',
    address: '88 Spring Street, New York, NY',
    zip: '10002',
    phone: '(212) 555-0288',
    insurance: 'Medicare Advantage',
    memberId: 'MA-332-10044',
    disposition: 'home_health',
    clinicalNeeds: ['wound_care', 'skilled_nursing'],
    urgency: 'routine',
    diagnoses: ['Post-surgical wound', 'Type 2 Diabetes', 'Peripheral neuropathy'],
    recentNotes: 'S/P right below-knee amputation. Wound healing progressing. Requires daily dressing changes and diabetic foot monitoring. Patient motivated and cooperative.',
    coordinationStatus: 'matched',
    facilityName: 'City Medical Center',
    assignedTo: 'Michael Chen',
    admitDate: '04/22/2026',
    attendingPhysician: 'Dr. James Okafor',
    losTargetDays: 7,
    insuranceTrack: {
      status: 'authorized',
      notes: 'Medicare Advantage covers wound care and skilled nursing. No prior auth required for initial 60-day home health episode.',
      checkedAt: '2026-04-25T14:01:00Z',
    },
    activityLog: [
      {
        id: 'ae-02-1',
        type: 'status_change',
        timestamp: '2026-04-22T09:15:00Z',
        author: 'System',
        content: 'Case opened. Patient admitted to City Medical Center.',
        isAgent: false,
      },
      {
        id: 'ae-02-2',
        type: 'call',
        timestamp: '2026-04-23T11:30:00Z',
        author: 'Michael Chen',
        content: 'Called patient\'s wife, Linda Chen. She confirmed patient is motivated for home discharge and will be available to assist with wound care setup. Agreed to home health plan.',
        isAgent: false,
      },
      {
        id: 'ae-02-3',
        type: 'status_change',
        timestamp: '2026-04-25T14:00:00Z',
        author: 'System',
        content: 'Matching complete. Providers identified and ranked.',
        isAgent: false,
      },
      {
        id: 'ae-02-4',
        type: 'agent',
        timestamp: '2026-04-25T14:01:00Z',
        author: 'Coordinate AI',
        content: 'Prior auth requirements checked for Medicare Advantage plan. Home wound care and skilled nursing services are covered with standard authorization. No gaps identified.',
        isAgent: true,
      },
    ],
    tasks: [
      {
        id: 'task-02-1',
        title: 'Confirm wound care supply delivery before discharge',
        status: 'open',
        priority: 'high',
        dueDate: '2026-05-02',
        assignee: 'Michael Chen',
        createdAt: '2026-04-29T14:02:00Z',
        isSystemGenerated: false,
      },
    ],
    patientContext: {
      consentDocumented: false,
      familyContacts: [
        {
          id: 'fc-02-1',
          name: 'Linda Chen',
          relationship: 'Wife',
          phone: '(212) 555-0291',
          isDecisionMaker: true,
          notes: 'Available afternoons. Will assist with wound care at home.',
        },
      ],
    },
  },
  {
    id: 'coord-03',
    name: 'Dorothy Williams',
    age: 81,
    dob: '11/05/1943',
    address: '330 East 18th Street, New York, NY',
    zip: '10003',
    phone: '(212) 555-0330',
    insurance: 'Medicare',
    memberId: 'M1A-901-33210',
    disposition: 'home_health',
    clinicalNeeds: ['pt_ot', 'medication_management'],
    urgency: 'routine',
    diagnoses: ['Hip fracture repair', 'Osteoporosis', 'Mild cognitive impairment'],
    recentNotes: 'Post ORIF left hip. Ambulating with walker. OT needed for ADL training and home safety evaluation. Family involved and supportive of discharge plan.',
    coordinationStatus: 'confirmed',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Sarah Johnson',
    admitDate: '04/20/2026',
    attendingPhysician: 'Dr. Marcus Webb',
    losTargetDays: 8,
  },
  {
    id: 'coord-04',
    name: 'James Patterson',
    age: 59,
    dob: '02/14/1966',
    address: '215 Hudson Street, New York, NY',
    zip: '10013',
    phone: '(212) 555-0215',
    insurance: 'Blue Cross',
    memberId: 'BC-774-98130',
    disposition: 'home_health',
    clinicalNeeds: ['iv_therapy', 'wound_care', 'skilled_nursing'],
    urgency: 'urgent',
    diagnoses: ['Osteomyelitis', 'PICC line in place', 'Hypertension'],
    recentNotes: 'Patient requires 6-week course of IV antibiotics for osteomyelitis. PICC line placed and functioning. Wound requires daily assessment. No barriers to home discharge identified.',
    coordinationStatus: 'awaiting_response',
    selectedProviderId: 'prov-03',
    facilityName: 'City Medical Center',
    assignedTo: 'Jennifer Martinez',
    admitDate: '04/21/2026',
    attendingPhysician: 'Dr. Priya Nair',
    losTargetDays: 6,
    insuranceTrack: {
      status: 'pending',
      notes: 'Prior auth submitted for home IV antibiotic therapy (6-week PICC course). Ref #BCBS-PA-2026-44121. Expected response 48–72 hours. Do not schedule start date until confirmed.',
      checkedAt: '2026-04-24T11:00:00Z',
    },
    providerOutreach: {
      startedAt: '2026-04-24T13:00:00Z',
      attempts: [
        {
          providerId: 'prov-03',
          providerName: 'Metropolitan Home Care',
          status: 'responded_yes',
          response: 'Spoke with intake coordinator Maria Santos. Can accept patient — IV antibiotic services available. Proposed start date Thursday April 25, contingent on auth confirmation.',
          attemptedAt: '2026-04-24T13:00:00Z',
          respondedAt: '2026-04-24T13:04:00Z',
        },
        {
          providerId: 'prov-01',
          providerName: 'Sunrise Home Health',
          status: 'responded_no',
          response: 'Spoke with intake. No capacity for IV antibiotic cases this week. Next availability May 3.',
          attemptedAt: '2026-04-24T13:00:00Z',
          respondedAt: '2026-04-24T13:07:00Z',
        },
        {
          providerId: 'prov-16',
          providerName: 'Greenfield SNF at Home',
          status: 'voicemail',
          response: 'Left voicemail with intake line. No callback after 4 hours. Follow-up scheduled.',
          attemptedAt: '2026-04-24T13:00:00Z',
          respondedAt: '2026-04-24T17:00:00Z',
        },
      ],
    },
    activityLog: [
      {
        id: 'ae-04-1',
        type: 'status_change',
        timestamp: '2026-04-21T08:00:00Z',
        author: 'System',
        content: 'Case opened. Patient admitted to City Medical Center.',
        isAgent: false,
      },
      {
        id: 'ae-04-2',
        type: 'status_change',
        timestamp: '2026-04-24T10:30:00Z',
        author: 'System',
        content: 'Matching complete. Metropolitan Home Care selected as preferred provider.',
        isAgent: false,
      },
      {
        id: 'ae-04-3',
        type: 'document',
        timestamp: '2026-04-24T13:15:00Z',
        author: 'System',
        content: 'Referral packet assembled. Documents: Discharge Summary, Physician Orders, Wound Care Assessment, Insurance Authorization.',
        isAgent: false,
      },
      {
        id: 'ae-04-4',
        type: 'status_change',
        timestamp: '2026-04-25T09:00:00Z',
        author: 'Jennifer Martinez',
        content: 'Referral sent to Metropolitan Home Care.',
        isAgent: false,
      },
      {
        id: 'ae-04-5',
        type: 'agent',
        timestamp: '2026-04-25T09:01:00Z',
        author: 'Coordinate AI',
        content: 'Follow-up task created: No response received within 24 hours. Recommend calling Metropolitan Home Care to confirm receipt.',
        isAgent: true,
      },
    ],
    tasks: [
      {
        id: 'task-04-1',
        title: 'Confirm referral received by Metropolitan Home Care',
        status: 'open',
        priority: 'high',
        dueDate: '2026-05-01',
        assignee: 'Jennifer Martinez',
        createdAt: '2026-04-30T09:01:00Z',
        isSystemGenerated: true,
      },
      {
        id: 'task-04-2',
        title: 'Confirm Blue Cross auth for home IV antibiotics before scheduling start date',
        status: 'open',
        priority: 'high',
        dueDate: '2026-05-02',
        assignee: 'Jennifer Martinez',
        createdAt: '2026-04-29T11:01:00Z',
        isSystemGenerated: false,
      },
    ],
    barriers: [
      {
        id: 'barrier-04-1',
        type: 'payer_auth',
        description: 'Blue Cross prior authorization for home IV antibiotic service (6-week PICC course) is pending. Referral sent optimistically — auth is a parallel track. Agent checking payer portal. Do not schedule start date until confirmed.',
        resolved: false,
        createdAt: '2026-04-24T11:00:00Z',
      },
    ],
    patientContext: {
      consentDocumented: true,
      consentNotes: 'Patient consented to home discharge plan and understands IV antibiotic schedule. Prefers morning visits.',
      consentTimestamp: '2026-04-24T10:00:00Z',
      familyContacts: [
        {
          id: 'fc-04-1',
          name: 'Karen Patterson',
          relationship: 'Wife',
          phone: '(212) 555-0216',
          isDecisionMaker: true,
          notes: 'Works from home. Comfortable managing PICC line care with instruction.',
        },
      ],
    },
  },
  {
    id: 'coord-05',
    name: 'Linda Garcia',
    age: 72,
    dob: '09/23/1952',
    address: '401 West 24th Street, New York, NY',
    zip: '10011',
    phone: '(212) 555-0401',
    insurance: 'Medicare',
    memberId: 'M1A-512-77403',
    disposition: 'home_health',
    clinicalNeeds: ['cardiac_monitoring', 'skilled_nursing', 'medication_management'],
    urgency: 'urgent',
    diagnoses: ['CABG post-op', 'Atrial fibrillation', 'Hypertension'],
    recentNotes: 'S/P 4-vessel CABG day 7. Sternotomy healing well. Requires cardiac telemetry monitoring, anticoagulation management. Cardiologist recommends home monitoring program.',
    coordinationStatus: 'pending',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Sarah Johnson',
    admitDate: '04/23/2026',
    attendingPhysician: 'Dr. Samuel Torres',
    losTargetDays: 7,
  },
  {
    id: 'coord-06',
    name: 'Thomas Anderson',
    age: 65,
    dob: '04/07/1960',
    address: '550 Park Avenue, New York, NY',
    zip: '10010',
    phone: '(212) 555-0550',
    insurance: 'Medicare Advantage',
    memberId: 'MA-441-20091',
    disposition: 'home_health',
    clinicalNeeds: ['speech_therapy', 'pt_ot', 'skilled_nursing'],
    urgency: 'routine',
    diagnoses: ['Ischemic stroke', 'Aphasia', 'Right-sided weakness'],
    recentNotes: 'Patient 10 days post ischemic stroke. Expressive aphasia improving. Requires speech and language therapy, PT for gait retraining. Daughter will assist with caregiving at home.',
    coordinationStatus: 'accepted',
    selectedProviderId: 'prov-19',
    facilityName: 'City Medical Center',
    assignedTo: 'Michael Chen',
    admitDate: '04/18/2026',
    attendingPhysician: 'Dr. Amara Singh',
    losTargetDays: 12,
    insuranceTrack: {
      status: 'authorized',
      notes: 'Medicare Advantage authorized speech therapy, PT, and skilled nursing. 60-day home health episode approved.',
      authNumber: 'MA-441-SLP-2026',
      checkedAt: '2026-04-22T13:30:00Z',
    },
    providerOutreach: {
      startedAt: '2026-04-22T13:00:00Z',
      attempts: [
        {
          providerId: 'prov-19',
          providerName: 'Beacon Speech & Rehab',
          status: 'responded_yes',
          response: 'Spoke with intake director. Confirmed capacity and prior relationship with patient. Start date April 28 confirmed.',
          attemptedAt: '2026-04-22T13:00:00Z',
          respondedAt: '2026-04-22T13:10:00Z',
        },
        {
          providerId: 'prov-04',
          providerName: 'Coastal Care Partners',
          status: 'responded_no',
          response: 'Spoke with intake. Can accept but patient preference is Beacon Speech & Rehab. Not selected.',
          attemptedAt: '2026-04-22T13:00:00Z',
          respondedAt: '2026-04-22T13:15:00Z',
        },
        {
          providerId: 'prov-01',
          providerName: 'Sunrise Home Health',
          status: 'responded_no',
          response: 'Spoke with intake. Do not offer speech therapy services.',
          attemptedAt: '2026-04-22T13:00:00Z',
          respondedAt: '2026-04-22T13:12:00Z',
        },
      ],
    },
    activityLog: [
      {
        id: 'ae-06-1',
        type: 'status_change',
        timestamp: '2026-04-18T07:30:00Z',
        author: 'System',
        content: 'Case opened. Patient admitted to City Medical Center.',
        isAgent: false,
      },
      {
        id: 'ae-06-2',
        type: 'call',
        timestamp: '2026-04-20T10:00:00Z',
        author: 'Michael Chen',
        content: 'Called daughter, Rachel Anderson. She confirmed she can provide 24hr care during initial home transition. Patient prefers Beacon Speech & Rehab due to prior relationship.',
        isAgent: false,
      },
      {
        id: 'ae-06-3',
        type: 'status_change',
        timestamp: '2026-04-22T14:00:00Z',
        author: 'System',
        content: 'Beacon Speech & Rehab selected. Availability confirmed. Referral packet assembled.',
        isAgent: false,
      },
      {
        id: 'ae-06-4',
        type: 'document',
        timestamp: '2026-04-22T14:05:00Z',
        author: 'System',
        content: 'Referral sent to Beacon Speech & Rehab.',
        isAgent: false,
      },
      {
        id: 'ae-06-5',
        type: 'agent',
        timestamp: '2026-04-23T09:00:00Z',
        author: 'Coordinate AI',
        content: 'Patient explanation of discharge options drafted. Ready for planner review.',
        isAgent: true,
      },
      {
        id: 'ae-06-6',
        type: 'status_change',
        timestamp: '2026-04-25T11:00:00Z',
        author: 'System',
        content: 'Referral accepted by Beacon Speech & Rehab. Start date: April 28.',
        isAgent: false,
      },
    ],
    tasks: [
      {
        id: 'task-06-1',
        title: 'Write EHR discharge planning note',
        status: 'open',
        priority: 'normal',
        dueDate: '2026-05-02',
        assignee: 'Michael Chen',
        createdAt: '2026-04-29T11:01:00Z',
        isSystemGenerated: false,
      },
    ],
    patientContext: {
      preferredProviderNotes: 'Patient strongly prefers Beacon Speech & Rehab — has worked with their speech therapist before.',
      homeEnvironmentNotes: 'Large apartment, accessible. Daughter Rachel (nurse) will live in during first 2 weeks.',
      consentDocumented: true,
      consentNotes: 'Discussed discharge plan with patient (using communication board) and daughter Rachel. Both understand and consent to plan.',
      consentTimestamp: '2026-04-22T13:00:00Z',
      familyContacts: [
        {
          id: 'fc-06-1',
          name: 'Rachel Anderson',
          relationship: 'Daughter',
          phone: '(212) 555-0551',
          isDecisionMaker: true,
          notes: 'RN. Primary caregiver during home transition. Comfortable with complex care needs.',
        },
      ],
    },
  },
  {
    id: 'coord-07',
    name: 'Patricia Moore',
    age: 77,
    dob: '06/19/1947',
    address: '78 Bleecker Street, New York, NY',
    zip: '10012',
    phone: '(212) 555-0078',
    insurance: 'Medicaid',
    memberId: 'MCD-228-40912',
    disposition: 'home_health',
    clinicalNeeds: ['skilled_nursing', 'wound_care', 'medication_management'],
    urgency: 'urgent',
    diagnoses: ['Stage 3 pressure ulcer', 'Diabetes', 'Protein malnutrition'],
    recentNotes: 'Large stage 3 sacral wound requiring advanced wound care. Nutritional supplementation initiated. Patient lives alone — home health aide and skilled nursing visit 5x/week needed.',
    coordinationStatus: 'confirm_failed',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Sarah Johnson',
    admitDate: '04/19/2026',
    attendingPhysician: 'Dr. Marcus Webb',
    losTargetDays: 7,
    insuranceTrack: {
      status: 'issue',
      notes: 'Medicaid home health aide authorization pending social work assessment. Medicaid waiver program enrollment may be required — no timeline confirmed. Coordinate with social work before scheduling.',
      checkedAt: '2026-04-21T09:30:00Z',
    },
    activityLog: [
      {
        id: 'ae-07-1',
        type: 'status_change',
        timestamp: '2026-04-19T08:00:00Z',
        author: 'System',
        content: 'Case opened. Patient admitted to Metro General Hospital.',
        isAgent: false,
      },
      {
        id: 'ae-07-2',
        type: 'note',
        timestamp: '2026-04-21T09:30:00Z',
        author: 'Sarah Johnson',
        content: 'Note: Patient lives alone. No family caregiver identified. Social work consult placed to assess home situation and possible aide authorization through Medicaid waiver.',
        isAgent: false,
      },
      {
        id: 'ae-07-3',
        type: 'status_change',
        timestamp: '2026-04-23T14:00:00Z',
        author: 'System',
        content: 'Matching complete. Greenfield SNF at Home selected as preferred provider.',
        isAgent: false,
      },
      {
        id: 'ae-07-4',
        type: 'status_change',
        timestamp: '2026-04-24T10:00:00Z',
        author: 'System',
        content: 'Confirmation failed — Greenfield SNF at Home is not accepting new Medicaid patients this week.',
        isAgent: false,
      },
      {
        id: 'ae-07-5',
        type: 'agent',
        timestamp: '2026-04-24T10:01:00Z',
        author: 'Coordinate AI',
        content: 'Barrier flagged: capacity issue with preferred provider. Metropolitan Home Care and Greenfield SNF at Home both score well as alternatives — recommend re-running matching.',
        isAgent: true,
      },
    ],
    barriers: [
      {
        id: 'barrier-07-1',
        type: 'capacity',
        description: 'Greenfield SNF at Home not accepting new Medicaid patients this week. Metropolitan Home Care is the next viable option.',
        resolved: false,
        createdAt: '2026-04-24T10:01:00Z',
      },
      {
        id: 'barrier-07-2',
        type: 'clinical_complexity',
        description: 'Patient lives alone — no in-home caregiver. Home health aide authorization through Medicaid waiver pending social work assessment.',
        resolved: false,
        createdAt: '2026-04-21T09:30:00Z',
      },
    ],
    patientContext: {
      homeEnvironmentNotes: 'Studio apartment. Patient lives alone. No elevator. Neighbors available but not reliable caregivers.',
      consentDocumented: false,
      familyContacts: [],
    },
  },
  // SNF at Home (3)
  {
    id: 'coord-08',
    name: 'Harold Jenkins',
    age: 83,
    dob: '01/30/1942',
    address: '19 Riverside Drive, New York, NY',
    zip: '10027',
    phone: '(212) 555-0019',
    insurance: 'Medicare',
    memberId: 'M1A-663-81920',
    disposition: 'snf_at_home',
    clinicalNeeds: ['skilled_nursing', 'pt_ot', 'medication_management', 'wound_care'],
    urgency: 'urgent',
    diagnoses: ['Hip replacement', 'COPD', 'Hypertension', 'Mild dementia'],
    recentNotes: 'S/P total hip arthroplasty. High complexity case — COPD with O2 requirement at baseline, cognitive impairment limits ability to follow hip precautions. SNF level care in home preferred by family.',
    coordinationStatus: 'pending',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Sarah Johnson',
    admitDate: '04/25/2026',
    attendingPhysician: 'Dr. Evelyn Park',
    losTargetDays: 6,
  },
  {
    id: 'coord-09',
    name: 'Barbara Kim',
    age: 79,
    dob: '08/14/1945',
    address: '200 West 108th Street, New York, NY',
    zip: '10025',
    phone: '(212) 555-0200',
    insurance: 'Medicare Advantage',
    memberId: 'MA-990-61230',
    disposition: 'snf_at_home',
    clinicalNeeds: ['skilled_nursing', 'cardiac_monitoring', 'medication_management', 'pt_ot'],
    urgency: 'urgent',
    diagnoses: ['Acute MI', 'Heart failure EF 30%', 'CKD stage 3'],
    recentNotes: 'Post-NSTEMI. EF significantly reduced. Requires daily cardiac assessments, fluid management, and dietary counseling. CKD complicates medication management. High readmission risk.',
    coordinationStatus: 'matching',
    facilityName: 'City Medical Center',
    assignedTo: 'Jennifer Martinez',
    admitDate: '04/23/2026',
    attendingPhysician: 'Dr. Samuel Torres',
    losTargetDays: 7,
  },
  {
    id: 'coord-10',
    name: 'William Foster',
    age: 76,
    dob: '12/03/1948',
    address: '345 Convent Avenue, New York, NY',
    zip: '10031',
    phone: '(212) 555-0345',
    insurance: 'Medicare',
    memberId: 'M1A-117-29034',
    disposition: 'snf_at_home',
    clinicalNeeds: ['skilled_nursing', 'wound_care', 'iv_therapy', 'medication_management'],
    urgency: 'urgent',
    diagnoses: ['Bilateral pneumonia', 'Sepsis recovery', 'Malnutrition'],
    recentNotes: 'Recovering from septic shock secondary to aspiration pneumonia. Requires IV antibiotics x 14 days, nutritional support, and respiratory monitoring. Complex but stable for home transition with intensive services.',
    coordinationStatus: 'pending',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Michael Chen',
    admitDate: '04/22/2026',
    attendingPhysician: 'Dr. Marcus Webb',
    losTargetDays: 8,
  },
  // Additional Home Health — manually added (no Predict)
  {
    id: 'coord-11',
    name: 'Susan Patel',
    age: 62,
    dob: '05/22/1963',
    address: '129 West 81st Street, New York, NY',
    zip: '10024',
    phone: '(212) 555-0129',
    insurance: 'Blue Cross',
    memberId: 'BC-559-33018',
    disposition: 'home_health',
    clinicalNeeds: ['iv_therapy', 'skilled_nursing', 'medication_management'],
    urgency: 'urgent',
    diagnoses: ['Acute pyelonephritis', 'Recurrent UTIs', 'Hypertension'],
    recentNotes: 'Patient completing IV antibiotic course for pyelonephritis. PICC line in place. Stable for home discharge with skilled nursing and IV therapy continuation.',
    coordinationStatus: 'pending',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Sarah Johnson',
    admitDate: '04/24/2026',
    attendingPhysician: 'Dr. Priya Nair',
    losTargetDays: 5,
  },
  // Additional Palliative — manually added (no Predict)
  {
    id: 'coord-12',
    name: 'Charles Robinson',
    age: 70,
    dob: '10/11/1954',
    address: '500 West 190th Street, New York, NY',
    zip: '10005',
    phone: '(212) 555-0500',
    insurance: 'Medicare',
    memberId: 'M1A-802-94712',
    disposition: 'palliative',
    clinicalNeeds: ['skilled_nursing', 'medication_management', 'cardiac_monitoring'],
    urgency: 'routine',
    diagnoses: ['Stage IV COPD', 'CAD', 'Chronic pain syndrome'],
    recentNotes: 'Patient with advanced COPD on supplemental O2. Goals of care aligned with comfort and quality of life. Palliative home care requested to manage symptoms and support family caregivers.',
    coordinationStatus: 'pending',
    facilityName: 'City Medical Center',
    assignedTo: 'Jennifer Martinez',
    admitDate: '04/26/2026',
    attendingPhysician: 'Dr. James Okafor',
    losTargetDays: 4,
  },
  // Additional SNF at Home — manually added (no Predict)
  {
    id: 'coord-13',
    name: 'Nancy Wright',
    age: 55,
    dob: '03/08/1970',
    address: '610 East 5th Street, New York, NY',
    zip: '10003',
    phone: '(212) 555-0610',
    insurance: 'Aetna',
    memberId: 'AET-334-10083',
    disposition: 'snf_at_home',
    clinicalNeeds: ['iv_therapy', 'wound_care', 'skilled_nursing', 'medication_management'],
    urgency: 'urgent',
    diagnoses: ['Necrotizing fasciitis — recovering', 'Sepsis recovery', 'Morbid obesity'],
    recentNotes: 'Patient recovering from necrotizing fasciitis of the lower extremity. Requires daily surgical wound care, IV antibiotics, and skilled nursing. High complexity — SNF at Home level services needed.',
    coordinationStatus: 'pending',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Sarah Johnson',
    admitDate: '04/20/2026',
    attendingPhysician: 'Dr. Amara Singh',
    losTargetDays: 10,
  },
  // Hospice (2)
  {
    id: 'coord-14',
    name: 'Edward Hughes',
    age: 79,
    dob: '09/17/1945',
    address: '22 Morton Street, New York, NY',
    zip: '10014',
    phone: '(212) 555-0022',
    insurance: 'Medicare',
    memberId: 'M1A-401-60278',
    disposition: 'hospice',
    clinicalNeeds: ['skilled_nursing', 'medication_management'],
    urgency: 'urgent',
    diagnoses: ['Stage IV pancreatic cancer', 'Cachexia', 'Chronic pain'],
    recentNotes: 'Patient and family have elected comfort-focused care. Goals of care conversation completed. Pain management optimized. Hospice benefit elected. Focus on comfort and quality of life at home.',
    coordinationStatus: 'confirmed',
    facilityName: 'City Medical Center',
    assignedTo: 'Jennifer Martinez',
    admitDate: '04/22/2026',
    attendingPhysician: 'Dr. Priya Nair',
    losTargetDays: 5,
  },
  {
    id: 'coord-15',
    name: 'Ruth Simmons',
    age: 85,
    dob: '12/28/1939',
    address: '88 Payson Avenue, New York, NY',
    zip: '10034',
    phone: '(212) 555-0088',
    insurance: 'Medicare',
    memberId: 'M1A-725-11490',
    disposition: 'hospice',
    clinicalNeeds: ['skilled_nursing', 'medication_management', 'speech_therapy'],
    urgency: 'urgent',
    diagnoses: ['End-stage COPD', 'Dementia', 'Failure to thrive'],
    recentNotes: 'Family meeting held regarding goals of care for end-stage COPD. DNR/DNI in place. Comfort measures only per patient wishes documented previously. Hospice transition planned.',
    coordinationStatus: 'pending',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Michael Chen',
    admitDate: '04/25/2026',
    attendingPhysician: 'Dr. Evelyn Park',
    losTargetDays: 4,
  },
  // Infusion at Home (3)
  {
    id: 'coord-16',
    name: 'Kevin Torres',
    age: 48,
    dob: '06/30/1977',
    address: '177 East 77th Street, New York, NY',
    zip: '10021',
    phone: '(212) 555-0177',
    insurance: 'Blue Cross',
    memberId: 'BC-882-31044',
    disposition: 'infusion_at_home',
    clinicalNeeds: ['iv_therapy', 'medication_management'],
    urgency: 'routine',
    diagnoses: ['Crohn\'s disease', 'Biologic therapy', 'Anemia'],
    recentNotes: 'Patient stable on biologic therapy for Crohn\'s. Transitioning to home infusion program for ongoing IV biologic administration. Excellent candidate — self-sufficient, good venous access.',
    coordinationStatus: 'matched',
    facilityName: 'City Medical Center',
    assignedTo: 'Jennifer Martinez',
    admitDate: '04/24/2026',
    attendingPhysician: 'Dr. James Okafor',
    losTargetDays: 3,
  },
  {
    id: 'coord-17',
    name: 'Maria Gonzalez',
    age: 53,
    dob: '11/15/1971',
    address: '290 Lenox Avenue, New York, NY',
    zip: '10027',
    phone: '(212) 555-0290',
    insurance: 'Aetna',
    memberId: 'AET-612-70021',
    disposition: 'infusion_at_home',
    clinicalNeeds: ['iv_therapy', 'skilled_nursing'],
    urgency: 'routine',
    diagnoses: ['Multiple myeloma', 'Zoledronic acid therapy', 'Bone lesions'],
    recentNotes: 'Patient on monthly IV bisphosphonate therapy for myeloma-related bone disease. Currently receiving in clinic — transitioning to home infusion for convenience and quality of life.',
    coordinationStatus: 'pending',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Sarah Johnson',
    admitDate: '04/26/2026',
    attendingPhysician: 'Dr. Marcus Webb',
    losTargetDays: 3,
  },
  {
    id: 'coord-18',
    name: 'David Kim',
    age: 41,
    dob: '02/08/1984',
    address: '415 West 115th Street, New York, NY',
    zip: '10025',
    phone: '(212) 555-0415',
    insurance: 'United',
    memberId: 'UHC-221-88430',
    disposition: 'infusion_at_home',
    clinicalNeeds: ['iv_therapy', 'medication_management'],
    urgency: 'urgent',
    diagnoses: ['Primary immunodeficiency', 'IVIG therapy', 'Recurrent infections'],
    recentNotes: 'Patient with CVID requiring monthly IVIG infusions. Currently hospitalized for recurrent sinopulmonary infection. Transitioning to home IVIG program to reduce hospital exposure.',
    coordinationStatus: 'pending',
    facilityName: 'City Medical Center',
    assignedTo: 'Michael Chen',
    admitDate: '04/23/2026',
    attendingPhysician: 'Dr. Samuel Torres',
    losTargetDays: 4,
  },
  // Palliative (2)
  {
    id: 'coord-19',
    name: 'Frances Mitchell',
    age: 71,
    dob: '08/04/1954',
    address: '55 East 9th Street, New York, NY',
    zip: '10003',
    phone: '(212) 555-0055',
    insurance: 'Medicare Advantage',
    memberId: 'MA-778-44012',
    disposition: 'palliative',
    clinicalNeeds: ['skilled_nursing', 'medication_management', 'pt_ot'],
    urgency: 'routine',
    diagnoses: ['ALS', 'Dysphagia', 'Respiratory compromise'],
    recentNotes: 'ALS patient with progressive decline. Discussing palliative goals with family. Remains at home with spouse as primary caregiver. Requires skilled nursing, PT to maintain function and prevent falls.',
    coordinationStatus: 'pending',
    facilityName: 'Metro General Hospital',
    assignedTo: 'Sarah Johnson',
    admitDate: '04/25/2026',
    attendingPhysician: 'Dr. Amara Singh',
    losTargetDays: 5,
  },
  {
    id: 'coord-20',
    name: 'Arthur Washington',
    age: 67,
    dob: '05/12/1958',
    address: '730 St Nicholas Avenue, New York, NY',
    zip: '10031',
    phone: '(212) 555-0730',
    insurance: 'Cigna',
    memberId: 'CGN-440-29011',
    disposition: 'palliative',
    clinicalNeeds: ['skilled_nursing', 'cardiac_monitoring', 'medication_management'],
    urgency: 'routine',
    diagnoses: ['Stage IV lung cancer', 'COPD', 'Chronic pain syndrome'],
    recentNotes: 'Patient with advanced lung cancer pursuing palliative approach. Enrolled in clinical trial, remains functional at ECOG 2. Palliative home health to manage symptoms, optimize comfort.',
    coordinationStatus: 'pending',
    facilityName: 'City Medical Center',
    assignedTo: 'Jennifer Martinez',
    admitDate: '04/27/2026',
    attendingPhysician: 'Dr. Priya Nair',
    losTargetDays: 4,
  },
];

// ── Insurance Type Derivation ────────────────────────────────────────────────

function deriveInsuranceType(insurance: string): CoordinatePatient['insuranceType'] {
  if (insurance === 'Medicare') return 'medicare';
  if (insurance === 'Medicare Advantage') return 'medicare_advantage';
  if (insurance === 'Medicaid') return 'medicaid';
  return 'commercial';
}

// ── Agent Draft Builders (Tier 2) ────────────────────────────────────────────

function buildProviderOutreachDraft(patient: CoordinatePatient, providerName: string): string {
  const services = patient.clinicalNeeds.map(n => n.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ');
  return `Subject: Home Health Referral — ${patient.name} (DOB: ${patient.dob})\n\nDear ${providerName} Care Team,\n\nWe are reaching out to confirm your availability for a new referral for ${patient.name}, a ${patient.age}-year-old patient currently admitted to ${patient.facilityName}.\n\nClinical Summary:\n- Diagnoses: ${patient.diagnoses.slice(0, 2).join(', ')}\n- Services Required: ${services}\n- Insurance: ${patient.insurance} (${patient.memberId})\n\nThis patient is a strong candidate for home-based care. A complete referral packet is available upon confirmation.\n\nPlease reply with your availability and capacity. We aim to coordinate discharge within the next 48–72 hours.\n\n${patient.assignedTo}\nDischarge Planning Coordinator\n${patient.facilityName}`;
}

function buildPatientExplanationDraft(patient: CoordinatePatient, providerName: string): string {
  const firstName = patient.name.split(' ')[0];
  const services = patient.clinicalNeeds.map(n => `- ${n.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`).join('\n');
  return `Dear ${firstName},\n\nWe are pleased to share the plan for your care following discharge from ${patient.facilityName}.\n\nBased on your clinical needs, we have arranged for ${providerName} to provide home-based services after you leave the hospital.\n\nServices you will receive at home:\n${services}\n\nA care team member will visit you at home to get started. If you have questions about your plan or the services you'll receive, please speak with your care coordinator, ${patient.assignedTo}.\n\nWe look forward to supporting your continued recovery.\n\n${patient.assignedTo}\nCare Coordinator, ${patient.facilityName}`;
}

function buildFamilyUpdateDraft(patient: CoordinatePatient, providerName: string): string {
  const firstName = patient.name.split(' ')[0];
  const contact = patient.patientContext?.familyContacts?.[0];
  const greeting = contact ? `Dear ${contact.name.split(' ')[0]},` : 'Dear Family,';
  const services = patient.clinicalNeeds.map(n => `- ${n.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`).join('\n');
  return `${greeting}\n\nI wanted to provide an update on ${firstName}'s discharge coordination from ${patient.facilityName}.\n\nWe have sent a referral to ${providerName} to provide home-based care services. ${providerName} is highly rated in your area and experienced in the care ${firstName} needs.\n\nServices being arranged:\n${services}\n\nCurrent status: We are awaiting confirmation of availability and start date. We expect a response within 24–48 hours and will follow up as soon as we have confirmation.\n\nPlease don't hesitate to reach out if you have any questions.\n\n${patient.assignedTo}\nCare Coordinator, ${patient.facilityName}`;
}

function buildAgencyNarrativeDraft(patient: CoordinatePatient, providerName: string): string {
  const needs = patient.clinicalNeeds.map(n => n.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ');
  const caregiverNote = patient.patientContext?.familyContacts?.find(c => c.isDecisionMaker)
    ? `Primary family contact is ${patient.patientContext!.familyContacts.find(c => c.isDecisionMaker)!.name} (${patient.patientContext!.familyContacts.find(c => c.isDecisionMaker)!.relationship}).`
    : 'No primary caregiver identified — please assess support needs at first visit.';
  const homeNote = patient.patientContext?.homeEnvironmentNotes
    ? `Home environment: ${patient.patientContext.homeEnvironmentNotes}`
    : 'Home environment assessment not documented — recommend evaluation at first visit.';

  return `To: ${providerName} — Intake Team\nFrom: ${patient.assignedTo}, Discharge Planning Coordinator\nRe: Home Health Referral — ${patient.name} (DOB: ${patient.dob})\n\n---\n\nCLINICAL OVERVIEW\n\n${patient.name} is a ${patient.age}-year-old patient currently admitted to ${patient.facilityName} under the care of ${patient.attendingPhysician}. Primary diagnoses include ${patient.diagnoses.slice(0, 2).join(' and ')}.\n\n${patient.recentNotes}\n\nSKILLED CARE NEEDS\n\nThis patient requires: ${needs}. Services should begin promptly upon discharge to maintain continuity of care.\n\nCLINICAL CONSIDERATIONS\n\n${patient.clinicalNeeds.includes('wound_care') ? '• Wound care: Dressing change protocol and wound assessment documentation required at each visit.\n' : ''}${patient.clinicalNeeds.includes('iv_therapy') ? '• IV therapy: PICC line in place. IV access assessment and antibiotic administration per physician orders.\n' : ''}${patient.clinicalNeeds.includes('cardiac_monitoring') ? '• Cardiac monitoring: Telemetry and vital sign trending. Notify physician for changes in rhythm or hemodynamics.\n' : ''}${patient.clinicalNeeds.includes('medication_management') ? '• Medication management: Review full medication list with patient/caregiver at first visit. High-alert medications require explicit teaching.\n' : ''}\nSOCIAL CONTEXT\n\n${caregiverNote} ${homeNote}\n\nInsurance: ${patient.insurance} | Member ID: ${patient.memberId}\n\nPlease confirm receipt and proposed start-of-care date at your earliest convenience. The complete clinical referral packet is attached.\n\n${patient.assignedTo}\nDischarge Planning Coordinator, ${patient.facilityName}`;
}

function buildStartOfCareFollowupDraft(patient: CoordinatePatient, providerName: string): string {
  return `To: ${providerName} — Intake / Care Coordination Team\nFrom: ${patient.assignedTo}, Discharge Planning, ${patient.facilityName}\nRe: Start of Care Confirmation — ${patient.name} (DOB: ${patient.dob})\n\n---\n\nThank you for accepting this referral. To complete our discharge coordination, we need to confirm a few details before ${patient.name} leaves the hospital:\n\n1. Confirmed start-of-care date and time\n2. Name of assigned clinician(s) for the first visit\n3. Confirmation that the patient or family has been contacted\n\nDischarge is anticipated imminently. Please respond as soon as possible — we want to ensure someone is at the home when the patient arrives.\n\nIf you have any questions or need additional clinical information, please contact me directly.\n\n${patient.assignedTo}\nDischarge Planning Coordinator, ${patient.facilityName}`;
}

// ── Referral Packet Builder ───────────────────────────────────────────────────

function buildReferralPacket(patient: CoordinatePatient, narrativeContent?: string): ReferralPacket {
  const now = new Date().toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const documents: ReferralDocument[] = [
    {
      id: `${patient.id}-doc-1`,
      title: 'Discharge Summary',
      type: 'discharge_summary',
      dateCreated: new Date().toLocaleDateString(),
      content: `DISCHARGE SUMMARY\n\nPatient: ${patient.name}\nDOB: ${patient.dob}\nAdmitting Diagnosis: ${patient.diagnoses[0]}\n\nHOSPITAL COURSE:\n${patient.recentNotes}\n\nDISCHARGE CONDITION: Stable\nDISCHARGE DISPOSITION: ${patient.disposition.replace(/_/g, ' ').toUpperCase()}\n\nFOLLOW-UP: Within 7 days of discharge with primary care physician.`,
    },
    {
      id: `${patient.id}-doc-2`,
      title: 'Physician Orders',
      type: 'physician_orders',
      dateCreated: new Date().toLocaleDateString(),
      content: `PHYSICIAN ORDERS FOR HOME CARE\n\nPatient: ${patient.name}\n\nORDERS:\n${patient.clinicalNeeds.map(n => `- ${n.replace(/_/g, ' ').toUpperCase()} per discipline evaluation and treatment plan`).join('\n')}\n\nFrequency: Per agency assessment and care plan\nDuration: 60 days with reassessment\n\nDiagnoses: ${patient.diagnoses.join(', ')}\n\nPhysician signature on file.`,
    },
  ];

  if (patient.clinicalNeeds.includes('pt_ot')) {
    documents.push({
      id: `${patient.id}-doc-3`,
      title: 'PT/OT Evaluation Notes',
      type: 'pt_ot_notes',
      dateCreated: new Date().toLocaleDateString(),
      content: `PHYSICAL & OCCUPATIONAL THERAPY EVALUATION\n\nPatient: ${patient.name}\n\nFUNCTIONAL STATUS:\n- Ambulation: Requires supervision to minimal assist\n- Transfers: Modified independence with assistive device\n- ADLs: Requires minimal to moderate assist for bathing/dressing\n\nRECOMMENDATIONS:\n- PT: Gait training, therapeutic exercise, fall prevention\n- OT: ADL retraining, home safety evaluation, adaptive equipment\n\nGoal: Maximize functional independence for safe home discharge.`,
    });
  }

  if (patient.clinicalNeeds.includes('wound_care')) {
    documents.push({
      id: `${patient.id}-doc-4`,
      title: 'Wound Care Assessment',
      type: 'wound_notes',
      dateCreated: new Date().toLocaleDateString(),
      content: `WOUND CARE ASSESSMENT\n\nPatient: ${patient.name}\n\nWOUND DESCRIPTION:\nLocation: Per nursing assessment\nStage/Classification: Requires ongoing evaluation\nSize: Measurements to be obtained at first home visit\n\nTREATMENT PLAN:\n- Dressing changes per wound care protocol\n- Wound measurement and photo documentation each visit\n- Notify physician if signs of infection or deterioration\n\nSUPPLIES ORDERED: Wound care supply kit to be delivered to home prior to first visit.`,
    });
  }

  documents.push({
    id: `${patient.id}-doc-5`,
    title: 'Insurance Authorization',
    type: 'insurance_auth',
    dateCreated: new Date().toLocaleDateString(),
    content: `INSURANCE PRIOR AUTHORIZATION\n\nPatient: ${patient.name}\nInsurance: ${patient.insurance}\nMember ID: ${patient.memberId}\n\nAUTHORIZATION STATUS: Approved\nAuthorized Services: Skilled home health services per physician orders\nAuthorized Units: Up to 60 visits over 60 days\nAuthorization Number: AUTH-${Math.floor(Math.random() * 900000 + 100000)}\n\nNote: Authorization subject to medical necessity review. Provider must notify payor of any changes to care plan.`,
  });

  if (narrativeContent) {
    documents.push({
      id: `${patient.id}-doc-narrative`,
      title: 'Agency Referral Narrative',
      type: 'agency_narrative',
      dateCreated: new Date().toLocaleDateString(),
      content: narrativeContent,
    });
  }

  return {
    assembledAt: now,
    patient: {
      name: patient.name,
      dob: patient.dob,
      address: patient.address,
      phone: patient.phone,
    },
    insurance: {
      plan: patient.insurance,
      memberId: patient.memberId,
    },
    clinicalSummary: {
      diagnoses: patient.diagnoses,
      recentNotes: patient.recentNotes,
      keyConditions: patient.diagnoses.slice(0, 3),
    },
    orders: patient.clinicalNeeds.map(n => n.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())),
    documents,
  };
}

// ── Provider ─────────────────────────────────────────────────────────────────

interface CoordinateProviderProps {
  children: ReactNode;
}

// ── Milestone task helpers ─────────────────────────────────────────────────

function buildMilestoneTasks(patient: CoordinatePatient, now: string): CaseTask[] {
  const insuranceType = patient.insuranceType ?? deriveInsuranceType(patient.insurance);
  const isMedicare = insuranceType === 'medicare';
  const ins = patient.insuranceTrack;
  const ctx = patient.patientContext;
  const status = patient.coordinationStatus;

  // Helper to check if a status is at or past a given point
  const atOrPast = (milestone: CoordinationStatus) => {
    const order: CoordinationStatus[] = [
      'pending', 'matching', 'matched', 'confirming', 'confirm_failed',
      'confirmed', 'awaiting_response', 'negotiating', 'accepted', 'declined',
    ];
    return order.indexOf(status) >= order.indexOf(milestone);
  };

  const tasks: CaseTask[] = [
    {
      id: `ms-${patient.id}-consent`,
      title: 'Document patient consent',
      status: ctx?.consentDocumented ? 'done' : 'open',
      priority: 'high',
      assignee: patient.assignedTo,
      createdAt: now,
      isSystemGenerated: true,
      milestoneKey: 'consent',
    },
    ...(isMedicare ? [
      {
        id: `ms-${patient.id}-homebound`,
        title: 'Confirm homebound status',
        status: ins?.homeboundDocumented ? 'done' as const : 'open' as const,
        priority: 'normal' as const,
        assignee: patient.assignedTo,
        createdAt: now,
        isSystemGenerated: true,
        milestoneKey: 'medicare_homebound' as const,
      },
      {
        id: `ms-${patient.id}-f2f`,
        title: 'Verify face-to-face encounter',
        status: ins?.f2fEncounterComplete ? 'done' as const : 'open' as const,
        priority: 'normal' as const,
        assignee: patient.assignedTo,
        createdAt: now,
        isSystemGenerated: true,
        milestoneKey: 'medicare_f2f' as const,
      },
      {
        id: `ms-${patient.id}-485`,
        title: 'Obtain signed Plan of Care (485)',
        status: ins?.physicianCertification485 === 'signed' ? 'done' as const : 'open' as const,
        priority: 'normal' as const,
        assignee: patient.assignedTo,
        createdAt: now,
        isSystemGenerated: true,
        milestoneKey: 'medicare_485' as const,
      },
    ] : [
      {
        id: `ms-${patient.id}-ins-auth`,
        title: 'Verify insurance authorization',
        status: (ins?.status === 'authorized' || ins?.status === 'not_required') ? 'done' as const : 'open' as const,
        priority: 'normal' as const,
        assignee: patient.assignedTo,
        createdAt: now,
        isSystemGenerated: true,
        milestoneKey: 'insurance_auth' as const,
      },
    ]),
    {
      id: `ms-${patient.id}-provider`,
      title: 'Select and confirm provider',
      status: atOrPast('confirmed') ? 'done' : 'open',
      priority: 'high',
      assignee: patient.assignedTo,
      createdAt: now,
      isSystemGenerated: true,
      milestoneKey: 'provider_confirmed',
    },
    {
      id: `ms-${patient.id}-referral`,
      title: 'Send referral',
      status: atOrPast('awaiting_response') ? 'done' : 'open',
      priority: 'high',
      assignee: patient.assignedTo,
      createdAt: now,
      isSystemGenerated: true,
      milestoneKey: 'referral_sent',
    },
    {
      id: `ms-${patient.id}-soc`,
      title: 'Confirm start of care date',
      status: patient.startOfCareDate ? 'done' : 'open',
      priority: 'normal',
      assignee: patient.assignedTo,
      createdAt: now,
      isSystemGenerated: true,
      milestoneKey: 'start_of_care',
    },
  ];

  return tasks;
}

function completeMilestone(tasks: CaseTask[], key: MilestoneKey): CaseTask[] {
  return tasks.map(t => t.milestoneKey === key && t.status !== 'done' ? { ...t, status: 'done' as const } : t);
}

function buildInitialPatients(): CoordinatePatient[] {
  return INITIAL_PATIENTS.map(p => {
    let result: CoordinatePatient = {
      ...p,
      insuranceType: p.insuranceType ?? deriveInsuranceType(p.insurance),
    } as CoordinatePatient;

    if (p.coordinationStatus !== 'pending' && p.coordinationStatus !== 'matching') {
      result = { ...result, matchResults: scoreProviders(result, MOCK_PROVIDERS) };
    }

    // Pre-seed Tier 2 agent drafts for demo patients
    if (p.id === 'coord-03') {
      // confirmed — patient explanation + agency narrative ready for Dorothy
      result = {
        ...result,
        selectedProviderId: 'prov-01',
        agentDrafts: [
          {
            id: 'draft-03-explain',
            type: 'patient_explanation',
            channel: 'portal' as const,
            content: buildPatientExplanationDraft(result, 'Sunrise Home Health'),
            status: 'pending',
            createdAt: '2026-04-27T10:00:00Z',
          },
          {
            id: 'draft-03-narrative',
            type: 'agency_narrative',
            content: buildAgencyNarrativeDraft(result, 'Sunrise Home Health'),
            status: 'pending',
            createdAt: '2026-04-27T10:01:00Z',
          },
        ],
      };
    }

    if (p.id === 'coord-04') {
      // awaiting_response — family update draft for Karen Patterson
      result = {
        ...result,
        agentDrafts: [{
          id: 'draft-04-family',
          type: 'family_update',
          channel: 'email' as const,
          content: buildFamilyUpdateDraft(result, 'Metropolitan Home Care'),
          status: 'pending',
          createdAt: '2026-04-25T09:02:00Z',
        }],
        // Greenfield voicemail task intentionally omitted — referral went to Metropolitan,
        // AI auto-cancelled the Greenfield follow-up when the referral was sent
      };
    }

    if (p.id === 'coord-06') {
      // accepted — patient explanation + start of care followup outstanding
      result = {
        ...result,
        startOfCareDate: '2026-04-28',
        agentDrafts: [
          {
            id: 'draft-06-explain',
            type: 'patient_explanation',
            channel: 'portal' as const,
            content: buildPatientExplanationDraft(result, 'Beacon Speech & Rehab'),
            status: 'pending',
            createdAt: '2026-04-22T14:06:00Z',
          },
        ],
      };
    }

    // Auto-build referral packet for any pre-seeded patient already past the confirmation step
    const packetStatuses: CoordinationStatus[] = ['confirmed', 'awaiting_response', 'negotiating', 'accepted', 'declined'];
    if (packetStatuses.includes(result.coordinationStatus) && !result.referralPacket) {
      result = { ...result, referralPacket: buildReferralPacket(result) };
    }

    // Seed milestone tasks — merge with any hand-authored tasks, milestones go first
    const seedDate = '2026-04-24T08:00:00Z';
    const milestones = buildMilestoneTasks(result, seedDate);
    const existingNonMilestone = (result.tasks ?? []).filter(t => !t.milestoneKey);
    result = { ...result, tasks: [...milestones, ...existingNonMilestone] };

    return result;
  });
}

export const CoordinateProvider: React.FC<CoordinateProviderProps> = ({ children }) => {
  const [patients, setPatients] = useState<CoordinatePatient[]>(buildInitialPatients);
  const patientsRef = useRef(patients);
  useEffect(() => { patientsRef.current = patients; }, [patients]);
  const [predictIntegrationEnabled, setPredictIntegrationEnabled] = useState(false);
  const [notifications, setNotifications] = useState<CoordinateNotification[]>([]);

  const addNotification = useCallback((n: Omit<CoordinateNotification, 'id' | 'createdAt'>) => {
    setNotifications(prev => [...prev, { ...n, id: `notif-${Date.now()}`, createdAt: new Date().toISOString() }]);
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const effectivePatients = predictIntegrationEnabled
    ? patients.map(p => ({
        ...p,
        predictProgram: p.predictProgram ?? DISPOSITION_LABELS[p.disposition],
      }))
    : patients;

  const updatePatient = useCallback((id: string, updates: Partial<CoordinatePatient>) => {
    setPatients(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
  }, []);

  const getPatientById = useCallback(
    (id: string) => effectivePatients.find(p => p.id === id),
    [effectivePatients]
  );

  const runMatching = useCallback((patientId: string) => {
    setPatients(prev => {
      const patient = prev.find(p => p.id === patientId);
      if (!patient) return prev;
      const startEntry: ActivityEntry = {
        id: `ae-${patientId}-match-start-${Date.now()}`,
        type: 'status_change',
        timestamp: new Date().toISOString(),
        author: 'System',
        content: 'Matching started.',
        isAgent: false,
      };
      return prev.map(p =>
        p.id === patientId
          ? { ...p, coordinationStatus: 'matching' as CoordinationStatus, matchResults: undefined, activityLog: [...(p.activityLog ?? []), startEntry] }
          : p
      );
    });
    setTimeout(() => {
      setPatients(prev => {
        const patient = prev.find(p => p.id === patientId);
        if (!patient) return prev;
        const results = scoreProviders(patient, MOCK_PROVIDERS);
        const topProvider = results[0]?.provider;
        const now = new Date().toISOString();

        const matchEntry: ActivityEntry = {
          id: `ae-${patientId}-matched-${Date.now()}`,
          type: 'status_change',
          timestamp: now,
          author: 'System',
          content: 'Matching complete. Providers identified and ranked.',
          isAgent: false,
        };
        const authEntry: ActivityEntry = {
          id: `ae-${patientId}-auth-${Date.now()}`,
          type: 'agent',
          timestamp: now,
          author: 'Coordinate AI',
          content: `Prior auth requirements checked for ${patient.insurance} plan. Required services are covered with standard authorization. No prior auth gaps identified.`,
          isAgent: true,
        };
        return prev.map(p =>
          p.id === patientId
            ? {
                ...p,
                coordinationStatus: 'matched' as CoordinationStatus,
                matchResults: results,
                lastMatchedAt: now,
                activityLog: [...(p.activityLog ?? []), matchEntry, authEntry],
              }
            : p
        );
      });
    }, 1500);
  }, []);

  const selectProvider = useCallback((patientId: string, providerId: string) => {
    updatePatient(patientId, { selectedProviderId: providerId });
  }, [updatePatient]);

  const confirmProvider = useCallback((patientId: string) => {
    // Check if AI outreach already confirmed capacity for this provider — skip the animation if so
    setPatients(prev => {
      const patient = prev.find(p => p.id === patientId);
      if (!patient) return prev;

      const outreachAlreadyConfirmed = patient.providerOutreach?.attempts.some(
        a => a.providerId === patient.selectedProviderId && a.status === 'responded_yes'
      );

      const providerName = patient.matchResults?.find(m => m.provider.id === patient.selectedProviderId)?.provider.name ?? 'Provider';
      const now = new Date().toISOString();

      if (outreachAlreadyConfirmed) {
        // Capacity already verified via AI outreach — go straight to confirmed
        const explanationDraft: AgentDraft = {
          id: `draft-${patientId}-explain-${Date.now()}`,
          type: 'patient_explanation',
          channel: 'portal',
          content: buildPatientExplanationDraft(patient, providerName),
          status: 'pending',
          createdAt: now,
        };
        const narrativeDraft: AgentDraft = {
          id: `draft-${patientId}-narrative-${Date.now()}`,
          type: 'agency_narrative',
          content: buildAgencyNarrativeDraft(patient, providerName),
          status: 'pending',
          createdAt: now,
        };
        return prev.map(p =>
          p.id === patientId
            ? {
                ...p,
                coordinationStatus: 'confirmed' as CoordinationStatus,
                referralPacket: buildReferralPacket(p),
                agentDrafts: [...(p.agentDrafts ?? []), explanationDraft, narrativeDraft],
                tasks: completeMilestone(p.tasks ?? [], 'provider_confirmed'),
              }
            : p
        );
      }

      // No prior outreach — start the confirming animation
      return prev.map(p =>
        p.id === patientId ? { ...p, coordinationStatus: 'confirming' as CoordinationStatus } : p
      );
    });

    // Resolve the confirming animation if we entered that state (no outreach skip)
    setTimeout(() => {
      setPatients(prev => {
        const patient = prev.find(p => p.id === patientId);
        if (!patient || patient.coordinationStatus !== 'confirming') return prev;

        const selectedMatch = patient.matchResults?.find(m => m.provider.id === patient.selectedProviderId);
        const capacity = selectedMatch?.provider.currentCapacitySignal;
        // High-capacity providers always succeed; others succeed with high probability
        const success = capacity === 'high' ? true : capacity === 'medium' ? Math.random() < 0.95 : Math.random() < 0.75;
        const now = new Date().toISOString();

        if (success) {
          const providerName = selectedMatch?.provider.name ?? 'Provider';
          const explanationDraft: AgentDraft = {
            id: `draft-${patientId}-explain-${Date.now()}`,
            type: 'patient_explanation',
            channel: 'portal',
            content: buildPatientExplanationDraft(patient, providerName),
            status: 'pending',
            createdAt: now,
          };
          const narrativeDraft: AgentDraft = {
            id: `draft-${patientId}-narrative-${Date.now()}`,
            type: 'agency_narrative',
            content: buildAgencyNarrativeDraft(patient, providerName),
            status: 'pending',
            createdAt: now,
          };
          return prev.map(p =>
            p.id === patientId
              ? {
                  ...p,
                  coordinationStatus: 'confirmed' as CoordinationStatus,
                  referralPacket: buildReferralPacket(p),
                  agentDrafts: [...(p.agentDrafts ?? []), explanationDraft, narrativeDraft],
                  tasks: completeMilestone(p.tasks ?? [], 'provider_confirmed'),
                }
              : p
          );
        } else {
          return prev.map(p =>
            p.id === patientId
              ? { ...p, coordinationStatus: 'confirm_failed' as CoordinationStatus, selectedProviderId: undefined }
              : p
          );
        }
      });
    }, 2000);
  }, []);

  const sendReferral = useCallback((patientId: string) => {
    setPatients(prev => {
      const patient = prev.find(p => p.id === patientId);
      if (!patient) return prev;
      const providerName = patient.matchResults?.find(m => m.provider.id === patient.selectedProviderId)?.provider.name ?? 'Provider';
      const now = new Date().toISOString();

      // Fold the pending narrative draft into the packet and mark it used
      const narrativeDraft = (patient.agentDrafts ?? []).find(d => d.type === 'agency_narrative' && d.status === 'pending');
      const packetWithNarrative = narrativeDraft
        ? buildReferralPacket(patient, narrativeDraft.content)
        : patient.referralPacket ?? buildReferralPacket(patient);

      const sendEntry: ActivityEntry = {
        id: `ae-${patientId}-sent-${Date.now()}`,
        type: 'status_change',
        timestamp: now,
        author: 'System',
        content: `Referral sent to ${providerName}.`,
        isAgent: false,
      };
      const followUpDueDate = new Date(new Date(now).getTime() + 86_400_000).toISOString().slice(0, 10);
      const followUpEntry: ActivityEntry = {
        id: `ae-${patientId}-followup-${Date.now()}`,
        type: 'agent',
        timestamp: now,
        author: 'Coordinate AI',
        content: `Referral sent to ${providerName}. I've scheduled a follow-up reminder for 24 hours from now — if no response is received by then, call ${providerName} to confirm receipt.`,
        isAgent: true,
      };
      const followUpTask: CaseTask = {
        id: `task-${patientId}-followup-${Date.now()}`,
        title: `Confirm referral received by ${providerName}`,
        status: 'open',
        priority: 'high',
        dueDate: followUpDueDate,
        assignee: patient.assignedTo,
        createdAt: now,
        isSystemGenerated: true,
      };
      const familyDraft: AgentDraft = {
        id: `draft-${patientId}-family-${Date.now()}`,
        type: 'family_update',
        channel: 'email',
        content: buildFamilyUpdateDraft(patient, providerName),
        status: 'pending',
        createdAt: now,
      };

      // Auto-complete voicemail follow-up tasks for providers that weren't selected
      const nonSelectedOutreachNames = (patient.providerOutreach?.attempts ?? [])
        .filter(a => a.providerId !== patient.selectedProviderId)
        .map(a => a.providerName);

      return prev.map(p =>
        p.id === patientId
          ? {
              ...p,
              coordinationStatus: 'awaiting_response' as CoordinationStatus,
              referralSentAt: now,
              referralPacket: packetWithNarrative,
              activityLog: [...(p.activityLog ?? []), sendEntry, followUpEntry],
              tasks: [
                ...completeMilestone(
                  (p.tasks ?? []).map(t =>
                    t.isSystemGenerated && nonSelectedOutreachNames.some(name => t.title.includes(name))
                      ? { ...t, status: 'done' as const }
                      : t
                  ),
                  'referral_sent'
                ),
                followUpTask,
              ],
              agentDrafts: [
                ...(p.agentDrafts ?? []).map(d =>
                  d.type === 'agency_narrative' && d.status === 'pending' ? { ...d, status: 'used' as const } : d
                ),
                familyDraft,
              ],
            }
          : p
      );
    });
  }, []);

  const simulateCapacityChange = useCallback((patientId: string) => {
    setPatients(prev => {
      const patient = prev.find(p => p.id === patientId);
      if (!patient) return prev;
      // Shuffle capacity signals to show dynamic re-ranking
      const signals: Array<'high' | 'medium' | 'low'> = ['high', 'medium', 'low'];
      const shuffledProviders = MOCK_PROVIDERS.map(p => ({
        ...p,
        currentCapacitySignal: signals[Math.floor(Math.random() * signals.length)],
      }));
      const results = scoreProviders(patient, shuffledProviders);
      return prev.map(p =>
        p.id === patientId
          ? { ...p, matchResults: results, lastMatchedAt: new Date().toISOString() }
          : p
      );
    });
  }, []);

  const updateDisposition = useCallback((patientId: string, disposition: DispositionType) => {
    setPatients(prev => {
      const patient = prev.find(p => p.id === patientId);
      if (!patient) return prev;
      const shouldRematch =
        patient.coordinationStatus === 'matched' || patient.coordinationStatus === 'confirm_failed';
      return prev.map(p =>
        p.id === patientId
          ? { ...p, disposition, coordinationStatus: shouldRematch ? 'pending' : p.coordinationStatus, matchResults: shouldRematch ? undefined : p.matchResults }
          : p
      );
    });
  }, []);

  const updateClinicalNeeds = useCallback((patientId: string, needs: ClinicalNeed[]) => {
    setPatients(prev => {
      const patient = prev.find(p => p.id === patientId);
      if (!patient) return prev;
      const shouldRematch =
        patient.coordinationStatus === 'matched' || patient.coordinationStatus === 'confirm_failed';
      return prev.map(p =>
        p.id === patientId
          ? { ...p, clinicalNeeds: needs, coordinationStatus: shouldRematch ? 'pending' : p.coordinationStatus, matchResults: shouldRematch ? undefined : p.matchResults }
          : p
      );
    });
  }, []);

  const updateUrgency = useCallback((patientId: string, urgency: UrgencyLevel) => {
    updatePatient(patientId, { urgency });
  }, [updatePatient]);

  const simulateReferralResponse = useCallback((patientId: string, response: 'accepted' | 'declined' | 'negotiating') => {
    if (response === 'negotiating') {
      setPatients(prev => {
        const patient = prev.find(p => p.id === patientId);
        if (!patient) return prev;
        const providerName = patient.matchResults?.find(m => m.provider.id === patient.selectedProviderId)?.provider.name ?? 'Provider';
        const negotiation: ProviderNegotiation = {
          providerName,
          startedAt: new Date().toISOString(),
          providerMessage: `We can accept ${patient.name} but have a few conditions we need resolved before confirming intake.`,
          conditions: [
            { id: 'neg-cond-1', description: 'Updated wound care assessment from attending physician required', status: 'pending' },
            { id: 'neg-cond-2', description: 'Start date pushed to Thursday — confirm patient and family agree', status: 'pending' },
          ],
        };
        const newEntry: ActivityEntry = {
          id: `ae-${patientId}-neg-${Date.now()}`,
          type: 'status_change',
          timestamp: new Date().toISOString(),
          author: 'System',
          content: `${providerName} responded with conditions. Case is now in negotiation.`,
          isAgent: false,
        };
        return prev.map(p =>
          p.id === patientId
            ? {
                ...p,
                coordinationStatus: 'negotiating',
                providerNegotiation: negotiation,
                activityLog: [...(p.activityLog ?? []), newEntry],
              }
            : p
        );
      });
    } else if (response === 'accepted') {
      setPatients(prev => {
        const patient = prev.find(p => p.id === patientId);
        if (!patient) return prev;
        const providerName = patient.matchResults?.find(m => m.provider.id === patient.selectedProviderId)?.provider.name ?? 'Provider';
        const now = new Date().toISOString();
        const socTask: CaseTask = {
          id: `task-${patientId}-soc-${Date.now()}`,
          title: `Confirm start-of-care date and assigned clinician with ${providerName}`,
          status: 'open',
          priority: 'high',
          assignee: patient.assignedTo,
          createdAt: now,
          isSystemGenerated: true,
        };
        const socDraft: AgentDraft = {
          id: `draft-${patientId}-soc-${Date.now()}`,
          type: 'start_of_care_followup',
          content: buildStartOfCareFollowupDraft(patient, providerName),
          status: 'pending',
          createdAt: now,
        };
        const acceptEntry: ActivityEntry = {
          id: `ae-${patientId}-accepted-${Date.now()}`,
          type: 'status_change',
          timestamp: now,
          author: 'System',
          content: `Referral accepted by ${providerName}.`,
          isAgent: false,
        };
        const socEntry: ActivityEntry = {
          id: `ae-${patientId}-soc-task-${Date.now()}`,
          type: 'agent',
          timestamp: now,
          author: 'Coordinate AI',
          content: `Start-of-care confirmation task created. Follow up with ${providerName} to confirm first visit date before patient leaves the hospital.`,
          isAgent: true,
        };
        return prev.map(p =>
          p.id === patientId
            ? {
                ...p,
                coordinationStatus: 'accepted' as CoordinationStatus,
                tasks: [...(p.tasks ?? []), socTask],
                agentDrafts: [...(p.agentDrafts ?? []), socDraft],
                activityLog: [...(p.activityLog ?? []), acceptEntry, socEntry],
              }
            : p
        );
      });
    } else {
      updatePatient(patientId, { coordinationStatus: 'declined' });
    }
  }, [updatePatient]);

  const addActivityEntry = useCallback((patientId: string, entry: Omit<ActivityEntry, 'id'>) => {
    setPatients(prev => prev.map(p =>
      p.id === patientId
        ? {
            ...p,
            activityLog: [
              ...(p.activityLog ?? []),
              { ...entry, id: `ae-${patientId}-${Date.now()}` },
            ],
          }
        : p
    ));
  }, []);

  const addTask = useCallback((patientId: string, task: Omit<CaseTask, 'id' | 'createdAt'>) => {
    setPatients(prev => prev.map(p =>
      p.id === patientId
        ? {
            ...p,
            tasks: [
              ...(p.tasks ?? []),
              { ...task, id: `task-${patientId}-${Date.now()}`, createdAt: new Date().toISOString() },
            ],
          }
        : p
    ));
  }, []);

  const updateTask = useCallback((patientId: string, taskId: string, updates: Partial<CaseTask>) => {
    setPatients(prev => prev.map(p => {
      if (p.id !== patientId) return p;
      const task = (p.tasks ?? []).find(t => t.id === taskId);
      const linkedBarrierId = task?.linkedBarrierId;
      const now = new Date().toISOString();
      return {
        ...p,
        tasks: (p.tasks ?? []).map(t => t.id === taskId ? { ...t, ...updates } : t),
        barriers: linkedBarrierId
          ? (p.barriers ?? []).map(b => {
              if (b.id !== linkedBarrierId) return b;
              if (updates.status === 'done') return { ...b, resolved: true, resolvedAt: now };
              if (updates.status === 'open') return { ...b, resolved: false, resolvedAt: undefined };
              return b;
            })
          : p.barriers,
      };
    }));
  }, []);

  const updatePatientContext = useCallback((patientId: string, context: Partial<PatientContext>) => {
    setPatients(prev => prev.map(p => {
      if (p.id !== patientId) return p;
      const updatedContext = { consentDocumented: false, familyContacts: [], ...(p.patientContext ?? {}), ...context };
      const tasks = context.consentDocumented ? completeMilestone(p.tasks ?? [], 'consent') : (p.tasks ?? []);
      return { ...p, patientContext: updatedContext, tasks };
    }));
  }, []);

  const addBarrier = useCallback((patientId: string, barrier: Omit<CaseBarrier, 'id' | 'createdAt'>) => {
    setPatients(prev => prev.map(p =>
      p.id === patientId
        ? {
            ...p,
            barriers: [
              ...(p.barriers ?? []),
              { ...barrier, id: `barrier-${patientId}-${Date.now()}`, createdAt: new Date().toISOString() },
            ],
          }
        : p
    ));
  }, []);

  const resolveBarrier = useCallback((patientId: string, barrierId: string) => {
    setPatients(prev => prev.map(p => {
      if (p.id !== patientId) return p;
      const now = new Date().toISOString();
      const barrier = (p.barriers ?? []).find(b => b.id === barrierId);
      return {
        ...p,
        barriers: (p.barriers ?? []).map(b =>
          b.id === barrierId ? { ...b, resolved: true, resolvedAt: now } : b
        ),
        tasks: barrier?.linkedTaskId
          ? (p.tasks ?? []).map(t => t.id === barrier.linkedTaskId ? { ...t, status: 'done' as const } : t)
          : p.tasks,
      };
    }));
  }, []);

  const updateNegotiationCondition = useCallback((patientId: string, conditionId: string, updates: Partial<NegotiationCondition>) => {
    setPatients(prev => prev.map(p =>
      p.id === patientId && p.providerNegotiation
        ? {
            ...p,
            providerNegotiation: {
              ...p.providerNegotiation,
              conditions: p.providerNegotiation.conditions.map(c =>
                c.id === conditionId ? { ...c, ...updates } : c
              ),
            },
          }
        : p
    ));
  }, []);

  const acceptNegotiation = useCallback((patientId: string) => {
    setPatients(prev => prev.map(p => {
      if (p.id !== patientId || !p.providerNegotiation) return p;
      const providerName = p.providerNegotiation.providerName;
      const now = new Date().toISOString();
      const acceptEntry: ActivityEntry = {
        id: `ae-${patientId}-accept-${Date.now()}`,
        type: 'status_change',
        timestamp: now,
        author: 'System',
        content: `All conditions resolved. Referral accepted by ${providerName}.`,
        isAgent: false,
      };
      const socEntry: ActivityEntry = {
        id: `ae-${patientId}-soc-neg-${Date.now()}`,
        type: 'agent',
        timestamp: now,
        author: 'Coordinate AI',
        content: `Start-of-care confirmation task created. Follow up with ${providerName} to confirm first visit date before patient leaves the hospital.`,
        isAgent: true,
      };
      const socTask: CaseTask = {
        id: `task-${patientId}-soc-neg-${Date.now()}`,
        title: `Confirm start-of-care date and assigned clinician with ${providerName}`,
        status: 'open',
        priority: 'high',
        assignee: p.assignedTo,
        createdAt: now,
        isSystemGenerated: true,
      };
      const socDraft: AgentDraft = {
        id: `draft-${patientId}-soc-neg-${Date.now()}`,
        type: 'start_of_care_followup',
        content: buildStartOfCareFollowupDraft(p, providerName),
        status: 'pending',
        createdAt: now,
      };
      return {
        ...p,
        coordinationStatus: 'accepted',
        providerNegotiation: { ...p.providerNegotiation, resolvedAt: now },
        activityLog: [...(p.activityLog ?? []), acceptEntry, socEntry],
        tasks: [...(p.tasks ?? []), socTask],
        agentDrafts: [...(p.agentDrafts ?? []), socDraft],
      };
    }));
  }, []);

  const useAgentDraft = useCallback((patientId: string, draftId: string) => {
    setPatients(prev => prev.map(p =>
      p.id === patientId
        ? {
            ...p,
            agentDrafts: (p.agentDrafts ?? []).map(d =>
              d.id === draftId ? { ...d, status: 'used' as const } : d
            ),
          }
        : p
    ));
  }, []);

  const editAgentDraft = useCallback((patientId: string, draftId: string, content: string) => {
    setPatients(prev => prev.map(p =>
      p.id === patientId
        ? {
            ...p,
            agentDrafts: (p.agentDrafts ?? []).map(d =>
              d.id === draftId ? { ...d, content } : d
            ),
          }
        : p
    ));
  }, []);

  const dismissAgentDraft = useCallback((patientId: string, draftId: string) => {
    setPatients(prev => prev.map(p =>
      p.id === patientId
        ? {
            ...p,
            agentDrafts: (p.agentDrafts ?? []).map(d =>
              d.id === draftId ? { ...d, status: 'dismissed' as const } : d
            ),
          }
        : p
    ));
  }, []);

  const checkInsuranceAuth = useCallback((patientId: string) => {
    setPatients(prev => prev.map(p =>
      p.id === patientId
        ? {
            ...p,
            insuranceTrack: {
              ...(p.insuranceTrack ?? {}),
              status: 'checking' as const,
              checkedAt: new Date().toISOString(),
            },
          }
        : p
    ));
    setTimeout(() => {
      setPatients(prev => {
        const patient = prev.find(p => p.id === patientId);
        if (!patient) return prev;
        const now = new Date().toISOString();
        const authEntry: ActivityEntry = {
          id: `ae-${patientId}-ins-auth-${Date.now()}`,
          type: 'agent',
          timestamp: now,
          author: 'Coordinate AI',
          content: `Insurance authorization verified for ${patient.insurance}. Services covered — no prior auth gaps identified.`,
          isAgent: true,
        };
        return prev.map(p =>
          p.id === patientId
            ? {
                ...p,
                insuranceTrack: {
                  ...(p.insuranceTrack ?? {}),
                  status: 'authorized' as const,
                  notes: `${patient.insurance} coverage verified. Required home health services are covered under standard authorization.`,
                  checkedAt: now,
                },
                barriers: (p.barriers ?? []).map(b =>
                  b.type === 'payer_auth' && !b.resolved
                    ? { ...b, resolved: true, resolvedAt: now }
                    : b
                ),
                tasks: completeMilestone(p.tasks ?? [], 'insurance_auth'),
                activityLog: [...(p.activityLog ?? []), authEntry],
              }
            : p
        );
      });
    }, 2500);
  }, []);

  const startProviderOutreach = useCallback((patientId: string) => {
    // Capture provider names synchronously from the ref so setTimeout callbacks
    // can call addNotification outside any state updater.
    const currentPatient = patientsRef.current.find(p => p.id === patientId);
    const top3Snapshot = currentPatient?.matchResults?.slice(0, 3) ?? [];
    const patientNameSnapshot = currentPatient?.name ?? '';

    setPatients(prev => {
      const patient = prev.find(p => p.id === patientId);
      if (!patient || !patient.matchResults) return prev;
      const top3 = patient.matchResults.slice(0, 3);
      const now = new Date().toISOString();
      const initialAttempts: ProviderOutreachAttempt[] = top3.map(m => ({
        providerId: m.provider.id,
        providerName: m.provider.name,
        status: 'calling' as const,
        attemptedAt: now,
      }));
      const outreachEntry: ActivityEntry = {
        id: `ae-${patientId}-outreach-${Date.now()}`,
        type: 'agent',
        timestamp: now,
        author: 'Coordinate AI',
        content: `Coordinate AI is calling ${top3.map(m => m.provider.name).join(', ')} simultaneously to check capacity and availability.`,
        isAgent: true,
      };
      return prev.map(p =>
        p.id === patientId
          ? {
              ...p,
              providerOutreach: { startedAt: now, attempts: initialAttempts },
              activityLog: [...(p.activityLog ?? []), outreachEntry],
            }
          : p
      );
    });

    // Simulate responses trickling in
    const responseData = [
      { delay: 5_000, index: 1, status: 'responded_no' as const,  response: 'Spoke with intake. At capacity for new admissions this week.' },
      { delay: 7_000, index: 2, status: 'voicemail' as const,      response: 'Left voicemail with intake line. No response yet — follow-up scheduled for tomorrow morning.' },
      { delay: 9_000, index: 0, status: 'responded_yes' as const,  response: 'Spoke with intake coordinator. Confirmed capacity and availability. Ready to accept referral.' },
    ];

    responseData.forEach(({ delay, index, status, response }) => {
      setTimeout(() => {
        const providerName = top3Snapshot[index]?.provider.name ?? '';

        setPatients(prev => {
          const patient = prev.find(p => p.id === patientId);
          if (!patient?.providerOutreach) return prev;
          const respondedAt = new Date().toISOString();
          const updatedAttempts = patient.providerOutreach.attempts.map((a, i) =>
            i === index ? { ...a, status, response, respondedAt } : a
          );
          const firstYes = updatedAttempts.find(a => a.status === 'responded_yes');
          const allDone = updatedAttempts.every(a => a.status !== 'calling');

          const attempt = updatedAttempts[index];
          const shortStatus = status === 'responded_yes' ? '✓ Available' : status === 'responded_no' ? '✗ Unavailable' : '→ Voicemail';
          const newEntries: ActivityEntry[] = [{
            id: `ae-${patientId}-response-${index}-${Date.now()}`,
            type: 'agent',
            timestamp: respondedAt,
            author: 'Coordinate AI',
            content: `${attempt.providerName}: ${shortStatus}. ${response}`,
            isAgent: true,
          }];

          if (allDone && firstYes) {
            newEntries.push({
              id: `ae-${patientId}-outreach-done-${Date.now()}`,
              type: 'agent',
              timestamp: respondedAt,
              author: 'Coordinate AI',
              content: `All calls complete. ${firstYes.providerName} confirmed availability. Select them to proceed.`,
              isAgent: true,
            });
          }

          const newTasks: CaseTask[] = [];
          const needsFollowUp: string[] = ['voicemail', 'no_answer'];
          if (needsFollowUp.includes(status)) {
            newTasks.push({
              id: `task-${patientId}-vm-${index}-${Date.now()}`,
              title: `Follow up with ${attempt.providerName} — left voicemail, awaiting callback`,
              status: 'open',
              priority: 'normal',
              assignee: patient.assignedTo,
              createdAt: respondedAt,
              isSystemGenerated: true,
            });
          }

          return prev.map(p =>
            p.id === patientId
              ? {
                  ...p,
                  providerOutreach: { ...p.providerOutreach!, attempts: updatedAttempts },
                  activityLog: [...(p.activityLog ?? []), ...newEntries],
                  tasks: newTasks.length ? [...(p.tasks ?? []), ...newTasks] : p.tasks,
                }
              : p
          );
        });

        if (status === 'responded_yes') {
          addNotification({
            patientId,
            patientName: patientNameSnapshot,
            message: `${providerName} confirmed availability for ${patientNameSnapshot}. Ready to select.`,
          });
        }
      }, delay);
    });
  }, []);

  const selectProviderFromOutreach = useCallback((patientId: string, providerId: string) => {
    setPatients(prev => prev.map(p =>
      p.id === patientId ? { ...p, selectedProviderId: providerId } : p
    ));
  }, []);

  const confirmStartOfCare = useCallback((patientId: string, date: string) => {
    setPatients(prev => {
      const patient = prev.find(p => p.id === patientId);
      if (!patient) return prev;
      const providerName = patient.matchResults?.find(m => m.provider.id === patient.selectedProviderId)?.provider.name ?? 'Provider';
      const entry: ActivityEntry = {
        id: `ae-${patientId}-soc-confirmed-${Date.now()}`,
        type: 'note',
        timestamp: new Date().toISOString(),
        author: patient.assignedTo,
        content: `Start of care confirmed with ${providerName}. First home visit: ${date}.`,
        isAgent: false,
      };
      return prev.map(p =>
        p.id === patientId
          ? {
              ...p,
              startOfCareDate: date,
              activityLog: [...(p.activityLog ?? []), entry],
              tasks: completeMilestone(p.tasks ?? [], 'start_of_care'),
            }
          : p
      );
    });
  }, []);

  const updateInsuranceDoc = useCallback((patientId: string, updates: Partial<InsuranceTrack>) => {
    setPatients(prev => prev.map(p => {
      if (p.id !== patientId) return p;
      const newTrack = { status: 'not_checked' as const, ...(p.insuranceTrack ?? {}), ...updates };
      let tasks = p.tasks ?? [];
      if (updates.homeboundDocumented) tasks = completeMilestone(tasks, 'medicare_homebound');
      if (updates.f2fEncounterComplete) tasks = completeMilestone(tasks, 'medicare_f2f');
      if (updates.physicianCertification485 === 'signed') tasks = completeMilestone(tasks, 'medicare_485');
      return { ...p, insuranceTrack: newTrack, tasks };
    }));
  }, []);

  return (
    <CoordinateContext.Provider
      value={{
        patients: effectivePatients,
        providers: MOCK_PROVIDERS,
        getPatientById,
        runMatching,
        selectProvider,
        confirmProvider,
        sendReferral,
        simulateCapacityChange,
        updateDisposition,
        updateClinicalNeeds,
        updateUrgency,
        simulateReferralResponse,
        predictIntegrationEnabled,
        setPredictIntegrationEnabled,
        addActivityEntry,
        addTask,
        updateTask,
        updatePatientContext,
        addBarrier,
        resolveBarrier,
        updateNegotiationCondition,
        acceptNegotiation,
        useAgentDraft,
        editAgentDraft,
        dismissAgentDraft,
        startProviderOutreach,
        selectProviderFromOutreach,
        checkInsuranceAuth,
        confirmStartOfCare,
        updateInsuranceDoc,
        notifications,
        dismissNotification,
      }}
    >
      {children}
    </CoordinateContext.Provider>
  );
};

export const useCoordinateContext = (): CoordinateContextType => {
  const context = useContext(CoordinateContext);
  if (!context) {
    throw new Error('useCoordinateContext must be used within CoordinateProvider');
  }
  return context;
};
